/**
 * 角色「今日设定」日历。`CharacterDiary.plan`
 *
 * ## 这一页存在的理由
 *
 * `plan` 是**人工写的高信息量共享设定**——「今晚6点在B站发布新歌」、
 * 「今天缈缈直播」、节日或新形态实验。它在当天进入对话的稳定前缀。
 *
 * 普通的「缈缈正在做什么」已归 CharacterMoment；没有人工设定时不会自动
 * 补一句日常安排。这页保留旧管理端「+ 添加安排」的人工排期能力。
 *
 * ## 与「梦灵日记」页的分工
 *
 * 日记页读的是 `content` / `summary`——LLM 次日凌晨回顾前一天写的，是**过去**。
 * 本页写的是 `plan`——**未来或今天的人工设定**。同一张表，两类内容，作者不同。
 *
 * 所以一个格子里是**两块独立的模块**，各带标签、各自可点：
 *
 *   [设定] …  → 打开今日设定编辑框（可写）
 *   [日记] …  → 打开日记阅读弹窗（只读，复用日记页的 `DiaryDetail`）
 *
 * 不合成一块的理由是它们的操作完全不同：设定在日历里编辑，日记在这里阅读；
 * 需要重写正文时从梦灵日记页发起。做成一块的话点下去只能二选一，另一个就得靠
 * 「再点一次别的地方」，那是猜谜不是界面。
 * 空白处点下去仍然是开设定编辑框——没排期的日子唯一能做的事就是排期。
 *
 * ## 一屏的邻月日期也要有内容
 *
 * 月面板固定 6×7 = 42 格，两头露出的上月尾巴与下月开头是真实可点的日期。
 * 只取当月会让它们永远空着，翻月时最需要对照的那几天反而看不见。
 * 服务端按 `[1号-7, 1号+42]` 取，见 `character-plan.service.ts` 的 `month()`。
 *
 * ## 可以给未来的日期排期
 *
 * 「下周五发新歌」得在周一就写好。日历向后翻随便点，服务端不限制日期。
 *
 * ## 变更理由只在改今天或更早时才要
 *
 * 未来的计划还没被任何一轮对话读到，改它没有后果可追；今天的正在往每一轮对话的
 * 稳定前缀里拼，过去的已经影响过真实对话。对前者强制填理由只会收获一堆「调整」
 * 「修改一下」，把后者里真正有信息的那些淹掉。判定与强制都在服务端
 * （`character-plan.service.ts` 的 `planReasonRequired`），本页只负责别显示多余的框。
 */

import { useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Badge,
  Button,
  Calendar,
  Card,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Spin,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { api } from "../../services/api";
import { useAuth } from "../../services/auth";
import { useCharacter } from "../../services/character";
import { DiaryDetail, type CharacterDiary } from "./CharacterDiariesPage";

interface PlanDay {
  id: string;
  dateString: string;
  title: string | null;
  plan: string | null;
  /**
   * 今日设定的来源。
   *
   * `generated` 是 2026-08-08 停用自动日计划前的历史记录；仅供审计，
   * 不再进对话或日记。`edited` 表示这类历史记录后来被运营改过，
   * 现在行为等同 `manual`。
   */
  planSource: "manual" | "generated" | "edited" | null;
  /** 日记正文写没写。决定编辑框里那句「改计划不会重写已生成的正文」出不出现。 */
  hasContent: boolean;
  /**
   * 日记的头一段，服务端截好的（优先 `summary`，缺了才退回 `content`）。
   *
   * 与 `hasContent` 不是一回事：摘要可能来自 `summary` 而 `content` 为空。
   */
  diaryExcerpt: string | null;
}

interface PlanMonth {
  month: string;
  /** 服务端实际取到的区间，含两头露出的邻月日期。 */
  from: string;
  to: string;
  /** 服务端按业务时区算的今天。免填理由的边界，**不要用浏览器本地时间代替**。 */
  today: string;
  days: PlanDay[];
}

/** 与服务端 `PLAN_MAX_CHARS` 保持一致。超长会挤占对话的缓存前缀。 */
const PLAN_MAX_CHARS = 500;

/**
 * 变更理由的预选项。
 *
 * 选中项的**文本原样落 `AuditLog.reason`**，所以写成一眼能看懂的完整短句，
 * 而不是「更正」这种在审计列表里等于没写的词。自己填排在最后一项，
 * 预选覆盖不到的情况才用——先给选项是为了让常见情形一秒选完，
 * 不给自由输入则会逼人在预选里硬凑，那比没有预选更糟。
 */
const REASON_PRESETS = [
  "补录当天漏排的安排",
  "更正写错的时间、平台或名称",
  "活动时间或内容有变更",
  "活动取消",
  "内容不妥或涉敏，紧急撤下",
];

/** 「自己填」这一项的哨兵值。不会被提交，提交的是 `reasonCustom` 的内容。 */
const CUSTOM_REASON = "__custom__";

interface PlanFormValues {
  title?: string;
  plan?: string;
  reasonPreset?: string;
  reasonCustom?: string;
}

/**
 * 日历格子里那两块的配色与标签。与卡片标题上的三个 Badge 一一对应。
 *
 * **颜色不是唯一标识，标签才是。** 只靠颜色的话，运营看到格子里有字
 * 得先想一下橙的是什么意思；而 `generated` 与运营写的混淆的代价是
 * 「以为已经排过期了」，那是漏排现实世界活动的直接原因。
 */
const MODULE_TONE = {
  plan: { label: "设定", color: "#1677ff", bg: "rgba(22, 119, 255, 0.08)" },
  generated: { label: "旧自动", color: "#d48806", bg: "rgba(212, 136, 6, 0.10)" },
  diary: { label: "日记", color: "#389e0d", bg: "rgba(56, 158, 13, 0.08)" },
} as const;

/**
 * 日历格子里的一块。计划和日记各占一块，各自可点，点开的东西不同。
 *
 * 两行截断而不是三行：一个格子里要塞下两块，antd 给的内容区只有 86px 高，
 * 各三行会溢出成滚动条，一眼看不全反而更差。完整文本挂在 `title` 上。
 *
 * ⚠ **`stopPropagation` 不能省。** 外层 `Calendar` 的 `onSelect` 挂在整个格子上，
 * 不拦住的话点日记会同时弹出日记和计划编辑框，两个叠在一起。
 */
function CellModule({
  tone,
  text,
  hint,
  onOpen,
}: {
  tone: keyof typeof MODULE_TONE;
  text: string;
  hint?: string;
  onOpen: () => void;
}) {
  const t = MODULE_TONE[tone];
  return (
    <div
      role="button"
      title={hint ?? text}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      style={{
        cursor: "pointer",
        color: t.color,
        background: t.bg,
        borderRadius: 3,
        padding: "1px 4px",
        marginTop: 2,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        wordBreak: "break-word",
      }}
    >
      <span style={{ fontWeight: 600, marginRight: 4 }}>{t.label}</span>
      {text}
    </div>
  );
}

export function CharacterPlansPage() {
  const { characterId } = useCharacter();
  const { can } = useAuth();
  const { message } = App.useApp();
  const qc = useQueryClient();

  const [month, setMonth] = useState(() => dayjs().format("YYYY-MM"));
  const [editing, setEditing] = useState<{ dateString: string; day?: PlanDay } | null>(null);
  /** 正在阅读的日记。只读，与 `editing` 互不相干，可以从这里跳到那里。 */
  const [reading, setReading] = useState<{ id: string; dateString: string } | null>(null);
  const [form] = Form.useForm<PlanFormValues>();
  const reasonPreset = Form.useWatch("reasonPreset", form);

  const editable = can("diary.plan");

  const { data, isFetching, error } = useQuery({
    queryKey: ["characterPlans", characterId, month],
    queryFn: () =>
      api.get<PlanMonth>("/api/v1/admin/diaries/character/plans", {
        characterId: characterId as string,
        month,
      }),
    enabled: Boolean(characterId),
    // 翻月时留住上一份数据。日历格子按完整日期取，不会串月；
    // 真正的目的是 `today` 别在加载间隙变成 undefined——它决定要不要显示理由框。
    placeholderData: keepPreviousData,
  });

  /**
   * 日记全文。点开才取。
   *
   * 不塞进日历那份响应里：一屏 42 格，`content` 均 394 字最长 740，
   * 整段带上会让每次翻月多传几十 KB，而一个月里真正点开细读的通常只有一两天。
   */
  const diary = useQuery({
    queryKey: ["characterDiaryDetail", reading?.id],
    queryFn: () =>
      api.get<CharacterDiary>("/api/v1/admin/diaries/character/detail", {
        id: reading?.id as string,
      }),
    enabled: reading !== null,
  });

  /**
   * 免填理由的边界。用服务端的今天，退化时才用浏览器的。
   *
   * 管理员的电脑设了别的时区就会和服务端差一天，那样前端会放行一个服务端必然
   * 400 的提交，或者反过来对未来日期多要一次理由。首屏加载完之前没有可用值，
   * 那一瞬间也点不开编辑框。
   */
  const today = data?.today ?? dayjs().format("YYYY-MM-DD");
  const reasonRequired = editing !== null && editing.dateString <= today;

  /** dateString → 当天数据。日历渲染要按天 O(1) 查。 */
  const byDate = useMemo(() => {
    const m = new Map<string, PlanDay>();
    for (const d of data?.days ?? []) m.set(d.dateString, d);
    return m;
  }, [data]);

  const save = useMutation({
    mutationFn: (values: { title: string | null; plan: string; reason: string | null }) =>
      api.post("/api/v1/admin/diaries/character/plans", {
        characterId,
        dateString: editing?.dateString,
        title: values.title,
        plan: values.plan,
        reason: values.reason,
      }),
    onSuccess: () => {
      message.success("已保存");
      setEditing(null);
      form.resetFields();
      // 不手工改本地状态，让缓存重新取。见 ReservedNamesPage 立的规矩。
      void qc.invalidateQueries({ queryKey: ["characterPlans"] });
      // 日记阅读器读的是同一张表，一并失效。
      void qc.invalidateQueries({ queryKey: ["characterDiaries"] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  /** 表单的两个理由字段 → 接口的一个字段。未来日期不带理由。 */
  function submit(v: PlanFormValues) {
    const reason = !reasonRequired
      ? null
      : v.reasonPreset === CUSTOM_REASON
        ? (v.reasonCustom ?? "").trim()
        : (v.reasonPreset ?? "");
    save.mutate({
      title: v.title?.trim() ? v.title.trim() : null,
      plan: v.plan ?? "",
      reason: reason || null,
    });
  }

  /** 打开某一天的计划编辑框。日历空白处、格子里的「计划」块、日记弹窗都走这里。 */
  function openPlan(dateString: string) {
    const day = byDate.get(dateString);
    setReading(null);
    setEditing({ dateString, day });
    form.setFieldsValue({
      title: day?.title ?? "",
      plan: day?.plan ?? "",
      reasonPreset: undefined,
      reasonCustom: "",
    });
  }

  if (!characterId) {
    return <Alert type="info" showIcon message="请先在顶部选择角色" />;
  }

  /** 正在编辑那天的既有数据。抽出来是为了让回调里不用写 `editing.day!`。 */
  const editingDay = editing?.day ?? null;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="今日设定是运营声明的共享事实"
        description={
          <span>
            这里只写节日、直播、作品发布、新形态实验等跨小时仍然有效的事实。
            缈缈普通的日常活动由 Moment 负责，不需要在这里每天填一条。
            日记正文（<Typography.Text code>content</Typography.Text>）由系统在次日凌晨自动生成，
            不在这里编辑。可以给未来的日期提前排期。
          </span>
        }
      />

      {error ? <Alert type="error" showIcon message={(error as Error).message} /> : null}
      {!editable ? (
        <Alert type="warning" showIcon message="你没有 diary.plan 权限，只能查看" />
      ) : null}

      <Card
        styles={{ body: { paddingTop: 8 } }}
        title={
          <Space size={16}>
            <span>今日设定</span>
            <Space size={8}>
              <Badge color="#1677ff" text="设定 · 运营写的" />
              <Badge color="#faad14" text="旧自动 · 仅供历史审计" />
              <Badge color="#52c41a" text="日记 · 次日凌晨生成" />
            </Space>
          </Space>
        }
      >
        <Spin spinning={isFetching}>
          <Calendar
            mode="month"
            onPanelChange={(value, mode) => {
              if (mode === "month") setMonth(value.format("YYYY-MM"));
            }}
            onSelect={(value, info) => {
              // 点隔壁月份的日期时 antd 会先切面板，别把它当编辑意图。
              // 格子里两块内容各自 stopPropagation 了，走到这里的只有空白处。
              if (info?.source === "date") openPlan(value.format("YYYY-MM-DD"));
            }}
            cellRender={(current, info) => {
              if (info.type !== "date") return info.originNode;
              const dateString = current.format("YYYY-MM-DD");
              const day = byDate.get(dateString);
              if (!day) return null;
              // 邻月的格子照样出内容——本页就是要连着看。但压暗一档，
              // 否则它跟当月的一样醒目，而 antd 只把日期数字调淡了，看着像 bug。
              const outside = dateString.slice(0, 7) !== month;
              return (
                <div style={{ fontSize: 12, lineHeight: 1.4, opacity: outside ? 0.5 : 1 }}>
                  {day.plan ? (
                    <CellModule
                      tone={day.planSource === "generated" ? "generated" : "plan"}
                      text={day.plan}
                      hint={
                        day.planSource === "generated"
                          ? `已停用的自动日计划，现在不影响对话；点开改写后会成为人工设定：${day.plan}`
                          : `点开编辑：${day.plan}`
                      }
                      onOpen={() => openPlan(dateString)}
                    />
                  ) : null}
                  {day.diaryExcerpt ? (
                    <CellModule
                      tone="diary"
                      text={day.diaryExcerpt}
                      hint={`点开读全文：${day.diaryExcerpt}`}
                      onOpen={() => setReading({ id: day.id, dateString })}
                    />
                  ) : null}
                </div>
              );
            }}
          />
        </Spin>
      </Card>

      <Modal
        open={editing !== null}
        title={`${editing?.dateString ?? ""} 的今日设定`}
        onCancel={() => {
          setEditing(null);
          form.resetFields();
        }}
        okText="保存"
        okButtonProps={{ disabled: !editable, loading: save.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        {editingDay && (editingDay.hasContent || editingDay.diaryExcerpt) ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="这一天的日记已经生成"
            description={
              <>
                <div>改今日设定不会重写已生成的日记正文，只影响还没发生的对话。</div>
                {editingDay.diaryExcerpt ? (
                  <Typography.Paragraph type="secondary" style={{ margin: "6px 0 0" }}>
                    {editingDay.diaryExcerpt}
                  </Typography.Paragraph>
                ) : null}
                {/* 不关掉编辑框，两层叠着开。日记是只读的，关掉它就回到还没保存的表单， */}
                {/* 反过来先关表单则会把已经敲进去的计划丢掉。 */}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() =>
                    setReading({ id: editingDay.id, dateString: editingDay.dateString })
                  }
                >
                  读这一天的日记全文
                </Button>
              </>
            }
          />
        ) : null}

        <Form form={form} layout="vertical" onFinish={submit} disabled={!editable}>
          <Form.Item name="title" label="标题" extra="可留空。日记生成时会被模型写的标题覆盖。">
            <Input maxLength={64} placeholder="给这一天起个名字" />
          </Form.Item>

          <Form.Item
            name="plan"
            label="今日设定"
            extra={`最多 ${PLAN_MAX_CHARS} 字。留空表示撤下这一天的人工设定；系统不会自动补写。`}
            rules={[{ max: PLAN_MAX_CHARS, message: `不能超过 ${PLAN_MAX_CHARS} 字` }]}
          >
            <Input.TextArea
              rows={5}
              showCount
              maxLength={PLAN_MAX_CHARS}
              placeholder="今晚6点会在B站、抖音、微博、小红书发布新歌，希望大家能喜欢~"
            />
          </Form.Item>

          {reasonRequired ? (
            <>
              <Form.Item
                name="reasonPreset"
                label="变更理由"
                extra={
                  editing?.dateString === today
                    ? "改的是今天。这一天的人工设定此刻正在往每一轮对话里拼，理由会进审计日志。"
                    : "改的是过去。这一天的人工设定已经影响过真实对话，理由会进审计日志。"
                }
                rules={[{ required: true, message: "选一个理由，或者选最后一项自己填" }]}
              >
                <Radio.Group>
                  <Space direction="vertical" size={4}>
                    {REASON_PRESETS.map((r) => (
                      <Radio key={r} value={r}>
                        {r}
                      </Radio>
                    ))}
                    <Radio value={CUSTOM_REASON}>其他（自己填）</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>

              {reasonPreset === CUSTOM_REASON ? (
                <Form.Item
                  name="reasonCustom"
                  rules={[
                    { required: true, whitespace: true, message: "写清楚为什么改这一天" },
                    { max: 200, message: "不能超过 200 字" },
                  ]}
                >
                  <Input.TextArea rows={2} showCount maxLength={200} placeholder="为什么改这一天" />
                </Form.Item>
              ) : null}
            </>
          ) : (
            <Typography.Text type="secondary">
              这是今天之后的日期，还没被任何一轮对话读到，改动不需要填理由。
            </Typography.Text>
          )}
        </Form>
      </Modal>

      {/*
        日记弹窗。只读——正文是日记管线的产物，人工不覆盖（见 ADMIN_CONSOLE.md §2.2.3）。
        版式直接复用日记页的 `DiaryDetail`，不另写一套：同一篇日记在两个地方长得
        不一样的话，运营会以为其中一处漏了字段。
      */}
      <Modal
        open={reading !== null}
        title={`${reading?.dateString ?? ""} 的日记`}
        width={760}
        onCancel={() => setReading(null)}
        destroyOnHidden
        footer={
          <Space>
            {/*
              两层叠着开的时候不给这个按钮：它会走 `openPlan()` 重置表单，
              把用户已经敲进去但还没保存的计划冲掉，而此刻编辑框就在下面开着。
            */}
            {reading && editing?.dateString !== reading.dateString ? (
              <Button onClick={() => openPlan(reading.dateString)}>改这一天的设定</Button>
            ) : null}
            <Button type="primary" onClick={() => setReading(null)}>
              关闭
            </Button>
          </Space>
        }
      >
        <Spin spinning={diary.isFetching}>
          {/* 撑一个最小高度：首次打开时还没数据，不给高度的话弹窗会先塌成一条再弹开。 */}
          <div style={{ minHeight: 240 }}>
            {diary.error ? (
              <Alert type="error" showIcon message={(diary.error as Error).message} />
            ) : null}
            {diary.data ? <DiaryDetail diary={diary.data} /> : null}
          </div>
        </Spin>
      </Modal>
    </Space>
  );
}
